import React, { useEffect, useState, useMemo } from 'react';
import { getStyling, updateStyling } from '../api/api';

const CATEGORIE_LABELS = {
  kwetsbaar: 'Kwetsbare gebieden',
  waterstaatswerk_water: 'Watergangen',
  waterstaatswerk_kering: 'Waterkeringen',
  polder: 'Polders / deelgebieden',
};

const PATROON_OPTIES = [
  { value: '', label: 'Geen' },
  { value: 'diagonaal_strepen', label: 'Diagonaal strepen' },
  { value: 'kruislings', label: 'Kruislings' },
  { value: 'stippen', label: 'Stippen' },
];

/* Convert hex (#rrggbb) + opacity → rgba string voor live preview-swatch. */
function hexNaarRgba(hex, opacity) {
  if (!hex || hex.length < 7) return `rgba(0,0,0,${opacity})`;
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return `rgba(${r}, ${g}, ${b}, ${opacity})`;
}

export default function KaartStijlen() {
  const [styling, setStyling] = useState([]);
  const [bezig, setBezig] = useState(true);
  const [error, setError] = useState(null);
  const [opslaanStatus, setOpslaanStatus] = useState({});  // type → 'opgeslagen' | 'fout'

  const laad = async () => {
    try {
      setBezig(true);
      const res = await getStyling();
      setStyling(res.data);
      setError(null);
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
    } finally {
      setBezig(false);
    }
  };

  useEffect(() => { laad(); }, []);

  const update = (type, key, value) => {
    setStyling(rows => rows.map(r => r.type === type ? { ...r, [key]: value } : r));
  };

  const opslaan = async (type) => {
    const row = styling.find(r => r.type === type);
    if (!row) return;
    try {
      const payload = {
        kleur_fill: row.kleur_fill,
        fill_opacity: parseFloat(row.fill_opacity),
        kleur_stroke: row.kleur_stroke,
        chip_bg: row.chip_bg,
        chip_text: row.chip_text,
        patroon: row.patroon || null,
        standaard_zichtbaar: !!row.standaard_zichtbaar,
        label: row.label,
        label_kort: row.label_kort,
      };
      await updateStyling(type, payload);
      setOpslaanStatus(s => ({ ...s, [type]: 'opgeslagen' }));
      setTimeout(() => {
        setOpslaanStatus(s => {
          const nieuw = { ...s };
          delete nieuw[type];
          return nieuw;
        });
      }, 2000);
    } catch (e) {
      setOpslaanStatus(s => ({ ...s, [type]: 'fout: ' + (e.response?.data?.detail || e.message) }));
    }
  };

  const grouped = useMemo(() => {
    const g = {};
    for (const r of styling) {
      if (!g[r.categorie]) g[r.categorie] = [];
      g[r.categorie].push(r);
    }
    return g;
  }, [styling]);

  if (bezig) return <div className="main-inner"><p>Laden…</p></div>;
  if (error) return <div className="main-inner"><p style={{ color: '#d70015' }}>Fout: {error}</p></div>;

  return (
    <div className="main-inner">
      <div className="page-header">
        <div>
          <h1 className="page-title">Kaart-stijlen</h1>
          <p className="page-subtitle">Pas de visuele opmaak aan van alle gebiedlagen die op kaarten worden getoond. Wijzigingen worden direct doorgevoerd voor alle gebruikers (na page-refresh).</p>
        </div>
      </div>

      {Object.keys(CATEGORIE_LABELS).map(cat => {
        const rows = grouped[cat] || [];
        if (rows.length === 0) return null;
        return (
          <div key={cat} className="card" style={{ marginBottom: 16 }}>
            <h3 style={{ fontSize: 16, fontWeight: 600, marginTop: 0, marginBottom: 12 }}>
              {CATEGORIE_LABELS[cat]}
            </h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              {rows.map(r => (
                <div
                  key={r.type}
                  style={{
                    display: 'grid',
                    gridTemplateColumns: '60px 1fr auto auto auto auto auto auto',
                    gap: 12,
                    alignItems: 'center',
                    padding: 8,
                    border: '1px solid var(--border)',
                    borderRadius: 'var(--radius)',
                  }}
                >
                  {/* Live preview swatch */}
                  <div
                    style={{
                      width: 60,
                      height: 36,
                      background: hexNaarRgba(r.kleur_fill, r.fill_opacity),
                      border: `2px solid ${r.kleur_stroke}`,
                      borderRadius: 4,
                    }}
                    title={`Preview voor ${r.type}`}
                  />

                  {/* Label-edits */}
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                    <input
                      type="text"
                      value={r.label || ''}
                      onChange={(e) => update(r.type, 'label', e.target.value)}
                      style={{ fontSize: 13, padding: '4px 6px', border: '1px solid var(--border)', borderRadius: 4 }}
                      title="Lange label (legenda + tooltips)"
                    />
                    <input
                      type="text"
                      value={r.label_kort || ''}
                      onChange={(e) => update(r.type, 'label_kort', e.target.value)}
                      style={{ fontSize: 12, padding: '4px 6px', border: '1px solid var(--border)', borderRadius: 4, color: 'var(--text-muted)' }}
                      title="Korte label (badge)"
                      placeholder="Korte naam"
                    />
                    <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>{r.type}</span>
                  </div>

                  {/* Fill-kleur + opacity */}
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
                    <span style={{ fontSize: 10, color: 'var(--text-muted)' }}>Vulling</span>
                    <input
                      type="color"
                      value={r.kleur_fill}
                      onChange={(e) => update(r.type, 'kleur_fill', e.target.value)}
                      style={{ width: 36, height: 28, border: 'none', background: 'transparent', cursor: 'pointer' }}
                    />
                  </div>

                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
                    <span style={{ fontSize: 10, color: 'var(--text-muted)' }}>Opaciteit</span>
                    <input
                      type="range"
                      min="0"
                      max="1"
                      step="0.01"
                      value={r.fill_opacity}
                      onChange={(e) => update(r.type, 'fill_opacity', parseFloat(e.target.value))}
                      style={{ width: 80 }}
                    />
                    <span style={{ fontSize: 11 }}>{(r.fill_opacity * 100).toFixed(0)}%</span>
                  </div>

                  {/* Stroke-kleur */}
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
                    <span style={{ fontSize: 10, color: 'var(--text-muted)' }}>Lijn</span>
                    <input
                      type="color"
                      value={r.kleur_stroke}
                      onChange={(e) => update(r.type, 'kleur_stroke', e.target.value)}
                      style={{ width: 36, height: 28, border: 'none', background: 'transparent', cursor: 'pointer' }}
                    />
                  </div>

                  {/* Patroon */}
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
                    <span style={{ fontSize: 10, color: 'var(--text-muted)' }}>Patroon</span>
                    <select
                      value={r.patroon || ''}
                      onChange={(e) => update(r.type, 'patroon', e.target.value || null)}
                      style={{ fontSize: 12, padding: '3px 6px', border: '1px solid var(--border)', borderRadius: 4 }}
                    >
                      {PATROON_OPTIES.map(o => (
                        <option key={o.value} value={o.value}>{o.label}</option>
                      ))}
                    </select>
                  </div>

                  {/* Standaard zichtbaar toggle */}
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 }}>
                    <span style={{ fontSize: 10, color: 'var(--text-muted)' }}>Standaard zichtbaar</span>
                    <label
                      style={{
                        position: 'relative',
                        display: 'inline-block',
                        width: 36,
                        height: 20,
                        cursor: 'pointer',
                      }}
                      title={r.standaard_zichtbaar ? 'Standaard zichtbaar voor alle gebruikers' : 'Standaard verborgen — gebruiker moet hem via legenda aanzetten'}
                    >
                      <input
                        type="checkbox"
                        checked={!!r.standaard_zichtbaar}
                        onChange={(e) => update(r.type, 'standaard_zichtbaar', e.target.checked)}
                        style={{ opacity: 0, width: 0, height: 0 }}
                      />
                      <span
                        style={{
                          position: 'absolute',
                          top: 0,
                          left: 0,
                          right: 0,
                          bottom: 0,
                          background: r.standaard_zichtbaar ? 'var(--accent)' : '#c7c7cc',
                          borderRadius: 20,
                          transition: 'background 0.15s ease',
                        }}
                      />
                      <span
                        style={{
                          position: 'absolute',
                          top: 2,
                          left: r.standaard_zichtbaar ? 18 : 2,
                          width: 16,
                          height: 16,
                          background: '#fff',
                          borderRadius: '50%',
                          transition: 'left 0.15s ease',
                          boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
                        }}
                      />
                    </label>
                  </div>

                  {/* Opslaan-knop */}
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 4, minWidth: 100 }}>
                    <button
                      onClick={() => opslaan(r.type)}
                      style={{
                        padding: '6px 12px',
                        border: 'none',
                        background: 'var(--accent)',
                        color: '#fff',
                        borderRadius: 'var(--radius)',
                        cursor: 'pointer',
                        fontSize: 13,
                      }}
                    >
                      Opslaan
                    </button>
                    {opslaanStatus[r.type] === 'opgeslagen' && (
                      <span style={{ fontSize: 11, color: '#34c759' }}>✓ opgeslagen</span>
                    )}
                    {opslaanStatus[r.type] && opslaanStatus[r.type].startsWith('fout') && (
                      <span style={{ fontSize: 11, color: '#d70015' }}>{opslaanStatus[r.type]}</span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
}
